(() => {
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number') return '';
    const fmt = moneyFormat || (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }
    // Fallback: USD-ish.
    return `$${(cents / 100).toFixed(2)}`;
  }

  function initQuantity(root) {
    const select = root.querySelector('select[data-qty-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const v = Math.max(1, parseInt(select.value, 10) || 1);
      if (String(v) !== select.value) select.value = String(v);
    });
  }

  function qtyMaxForVariant(variant, variantQuantityMax) {
    if (variant && variantQuantityMax && typeof variantQuantityMax[variant.id] === 'number') {
      return Math.max(1, variantQuantityMax[variant.id]);
    }
    return 20;
  }

  function updateQtySelect(root, variant, variantQuantityMax) {
    const sel = root.querySelector('select[data-qty-select]');
    if (!sel) return;
    const max = qtyMaxForVariant(variant, variantQuantityMax);
    const prev = Math.min(Math.max(1, parseInt(sel.value, 10) || 1), max);
    sel.innerHTML = '';
    for (let i = 1; i <= max; i += 1) {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = String(i);
      if (i === prev) opt.selected = true;
      sel.appendChild(opt);
    }
  }

  function initProductGallery(gallery) {
    const viewport = gallery.querySelector('[data-product-gallery-viewport]');
    if (!viewport) return;

    const slides = Array.from(viewport.querySelectorAll('[data-product-gallery-slide]'));
    if (slides.length <= 1) return;

    const container = gallery.closest('.product_images-container');
    const dots = container?.querySelector('[data-product-gallery-dots]');
    const dotButtons = dots ? Array.from(dots.querySelectorAll('[data-product-gallery-dot]')) : [];
    const thumbnailsEl = container?.querySelector('[data-product-thumbnails]');
    const thumbnails = thumbnailsEl ? Array.from(thumbnailsEl.querySelectorAll('.product-images-thumbnail')) : [];
    const prevBtn = gallery.querySelector('[data-product-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-product-gallery-next]');

    let index = 0;

    function updateThumbnailsVisibility() {
      if (!thumbnailsEl) return;
      const canScrollHorizontally = viewport.scrollWidth > (viewport.clientWidth + 1);
      thumbnailsEl.style.display = canScrollHorizontally ? '' : 'none';
    }

    function setActive(nextIndex) {
      index = ((nextIndex % slides.length) + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('w-active', i === index));
      dotButtons.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === index);
        btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      thumbnails?.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      prevBtn?.classList.toggle('at-edge', index === 0);
      nextBtn?.classList.toggle('at-edge', index === slides.length - 1);
    }

    function goTo(i, behavior = 'smooth') {
      index = ((i % slides.length) + slides.length) % slides.length;
      setActive(index);
      const x = index * viewport.clientWidth;
      viewport.scrollTo({ left: x, behavior });
    }

    dotButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const i = Number(btn.getAttribute('data-index'));
        if (!Number.isNaN(i)) goTo(i);
      });
    });

    thumbnails?.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const i = Number(thumb.getAttribute('data-thumbnail-index'));
        if (!Number.isNaN(i)) goTo(i);
      });
    });

    prevBtn?.addEventListener('click', (e) => { e.preventDefault(); goTo(index - 1); });
    nextBtn?.addEventListener('click', (e) => { e.preventDefault(); goTo(index + 1); });

    let raf = 0;
    viewport.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = viewport.clientWidth || 1;
        setActive(Math.round(viewport.scrollLeft / w));
      });
    }, { passive: true });

    window.addEventListener('resize', () => {
      goTo(index, 'auto');
      updateThumbnailsVisibility();
    });

    goTo(0, 'auto');
    updateThumbnailsVisibility();
  }

  /** @typedef {{ src: string, thumb: string, w: number, h: number, alt: string }} ProductGalleryImage */

  function getObjectFitContainRect(containerW, containerH, natW, natH) {
    if (!natW || !natH || !containerW || !containerH) {
      return { ox: 0, oy: 0, w: containerW, h: containerH, scale: 1 };
    }
    const r = Math.min(containerW / natW, containerH / natH);
    const w = natW * r;
    const h = natH * r;
    const ox = (containerW - w) / 2;
    const oy = (containerH - h) / 2;
    return { ox, oy, w, h, scale: r };
  }

  function initProductLightbox(root) {
    const host = root.querySelector('[data-enable-product-lightbox="true"]');
    const lightbox = root.querySelector('[data-product-lightbox]');
    const jsonEl = root.querySelector('[data-product-gallery-images-json]');
    if (!host || !lightbox || !jsonEl) return;

    /** @type {ProductGalleryImage[]} */
    let images = [];
    try {
      images = JSON.parse(jsonEl.textContent || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(images) || images.length === 0) return;

    const slides = Array.from(host.querySelectorAll('[data-product-gallery-slide][data-product-gallery-slide-index]'));
    const stage = lightbox.querySelector('[data-product-lightbox-stage]');
    const pan = lightbox.querySelector('[data-product-lightbox-pan]');
    const img = lightbox.querySelector('[data-product-lightbox-img]');
    const lens = lightbox.querySelector('[data-product-lightbox-lens]');
    const thumbsWrap = lightbox.querySelector('[data-product-lightbox-thumbs]');
    const zoomIn = lightbox.querySelector('[data-product-lightbox-zoom-in]');
    const zoomOut = lightbox.querySelector('[data-product-lightbox-zoom-out]');
    if (!stage || !pan || !img || !lens || !thumbsWrap) return;

    const mqDesktop = window.matchMedia('(min-width: 992px)');
    let index = 0;
    let scale = 1;
    let panX = 0;
    let panY = 0;
    /** @type {HTMLElement[]} */
    let thumbEls = [];
    let lastFocus = null;
    let isOpen = false;

    let pinchStartDist = 0;
    let pinchStartScale = 1;
    let mouseDrag = false;
    let touchPan = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let panStartX = 0;
    let panStartY = 0;

    const LENS_SIZE = 140;
    const LENS_ZOOM = 2.5;
    const SCALE_MIN = 1;
    const SCALE_MAX = 4;
    const SCALE_STEP = 1.2;

    function syncThumbs() {
      thumbEls.forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    function applyTransform() {
      pan.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
    }

    function clampPan() {
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      if (!sw || !sh) return;
      const maxX = Math.max(0, (sw * scale - sw) / 2);
      const maxY = Math.max(0, (sh * scale - sh) / 2);
      panX = Math.min(maxX, Math.max(-maxX, panX));
      panY = Math.min(maxY, Math.max(-maxY, panY));
    }

    function resetView() {
      scale = 1;
      panX = 0;
      panY = 0;
      applyTransform();
      lens.hidden = true;
    }

    function setIndex(i) {
      index = ((i % images.length) + images.length) % images.length;
      const item = images[index];
      resetView();
      img.alt = item.alt || '';
      img.src = item.src;
      img.removeAttribute('srcset');
      syncThumbs();
    }

    function buildThumbs() {
      thumbsWrap.innerHTML = '';
      thumbEls = images.map((item, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-lightbox__thumb';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.setAttribute('aria-label', `Image ${i + 1}`);
        btn.dataset.index = String(i);
        const im = document.createElement('img');
        im.src = item.thumb;
        im.alt = '';
        im.className = 'product-lightbox__thumb-img';
        im.loading = 'lazy';
        im.draggable = false;
        btn.appendChild(im);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          setIndex(i);
        });
        thumbsWrap.appendChild(btn);
        return btn;
      });
      thumbsWrap.style.display = images.length <= 1 ? 'none' : '';
    }

    function touchDistance(t0, t1) {
      const dx = t0.clientX - t1.clientX;
      const dy = t0.clientY - t1.clientY;
      return Math.hypot(dx, dy) || 1;
    }

    function updateLens(clientX, clientY) {
      if (!mqDesktop.matches || scale > 1.05 || !img.complete || !img.naturalWidth) {
        lens.hidden = true;
        return;
      }

      const imgRect = img.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      const natW = img.naturalWidth;
      const natH = img.naturalHeight;
      const inner = getObjectFitContainRect(imgRect.width, imgRect.height, natW, natH);

      const mx = clientX - imgRect.left;
      const my = clientY - imgRect.top;
      const ix = mx - inner.ox;
      const iy = my - inner.oy;
      if (ix < 0 || iy < 0 || ix > inner.w || iy > inner.h) {
        lens.hidden = true;
        return;
      }

      const lensHalf = LENS_SIZE / 2;
      const cxStage = clientX - stageRect.left;
      const cyStage = clientY - stageRect.top;
      const imgL = imgRect.left - stageRect.left;
      const imgT = imgRect.top - stageRect.top;
      let lx = cxStage - lensHalf;
      let ly = cyStage - lensHalf;
      lx = Math.min(Math.max(imgL, lx), imgL + imgRect.width - LENS_SIZE);
      ly = Math.min(Math.max(imgT, ly), imgT + imgRect.height - LENS_SIZE);

      lens.hidden = false;
      lens.style.width = `${LENS_SIZE}px`;
      lens.style.height = `${LENS_SIZE}px`;
      lens.style.left = `${lx}px`;
      lens.style.top = `${ly}px`;
      lens.style.backgroundImage = 'url(' + JSON.stringify(img.src) + ')';

      const bgW = inner.w * LENS_ZOOM;
      const bgH = inner.h * LENS_ZOOM;
      const bgX = lensHalf - ix * LENS_ZOOM;
      const bgY = lensHalf - iy * LENS_ZOOM;
      lens.style.backgroundSize = `${bgW}px ${bgH}px`;
      lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
    }

    function openAt(i) {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      setIndex(i);
      lightbox.setAttribute('aria-hidden', 'false');
      lightbox.querySelector('.product-lightbox__close')?.focus?.();
    }

    function closeLb() {
      if (!isOpen) return;
      isOpen = false;
      mouseDrag = false;
      touchPan = false;
      lightbox.hidden = true;
      document.body.style.overflow = '';
      lightbox.setAttribute('aria-hidden', 'true');
      lens.hidden = true;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function zoomBy(factor) {
      const next = Math.min(SCALE_MAX, Math.max(SCALE_MIN, scale * factor));
      scale = next;
      if (scale <= 1.01) {
        panX = 0;
        panY = 0;
      }
      clampPan();
      applyTransform();
      if (scale > 1.05) lens.hidden = true;
    }

    buildThumbs();
    setIndex(0);
    lightbox.hidden = true;

    slides.forEach((slide) => {
      slide.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = Number(slide.getAttribute('data-product-gallery-slide-index'));
        if (Number.isNaN(idx)) return;
        openAt(idx);
      });
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const idx = Number(slide.getAttribute('data-product-gallery-slide-index'));
          if (!Number.isNaN(idx)) openAt(idx);
        }
      });
    });

    lightbox.querySelectorAll('[data-product-lightbox-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        closeLb();
      });
    });

    zoomIn?.addEventListener('click', (e) => {
      e.preventDefault();
      zoomBy(SCALE_STEP);
    });
    zoomOut?.addEventListener('click', (e) => {
      e.preventDefault();
      zoomBy(1 / SCALE_STEP);
    });

    stage.addEventListener('mousemove', (e) => {
      if (!isOpen || !mqDesktop.matches) return;
      const r = stage.getBoundingClientRect();
      if (
        e.clientX < r.left
        || e.clientX > r.right
        || e.clientY < r.top
        || e.clientY > r.bottom
      ) {
        lens.hidden = true;
        return;
      }
      updateLens(e.clientX, e.clientY);
    });

    stage.addEventListener('mouseleave', () => {
      lens.hidden = true;
    });

    stage.addEventListener(
      'wheel',
      (e) => {
        if (!isOpen || !mqDesktop.matches) return;
        e.preventDefault();
        if (e.deltaY < 0) zoomBy(1.08);
        else zoomBy(1 / 1.08);
      },
      { passive: false },
    );

    stage.addEventListener('mousedown', (e) => {
      if (!isOpen || !mqDesktop.matches || scale <= 1.01 || e.button !== 0) return;
      mouseDrag = true;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      panStartX = panX;
      panStartY = panY;
    });

    window.addEventListener('mousemove', (e) => {
      if (!mouseDrag) return;
      panX = panStartX + (e.clientX - dragStartX);
      panY = panStartY + (e.clientY - dragStartY);
      clampPan();
      applyTransform();
    });

    window.addEventListener('mouseup', () => {
      mouseDrag = false;
    });

    stage.addEventListener(
      'touchstart',
      (e) => {
        if (!isOpen) return;
        if (e.touches.length === 2) {
          touchPan = false;
          pinchStartDist = touchDistance(e.touches[0], e.touches[1]);
          pinchStartScale = scale;
        } else if (e.touches.length === 1 && scale > 1.05) {
          touchPan = true;
          dragStartX = e.touches[0].clientX;
          dragStartY = e.touches[0].clientY;
          panStartX = panX;
          panStartY = panY;
        }
      },
      { passive: true },
    );

    stage.addEventListener(
      'touchmove',
      (e) => {
        if (!isOpen) return;
        if (e.touches.length === 2) {
          e.preventDefault();
          const d = touchDistance(e.touches[0], e.touches[1]);
          if (pinchStartDist < 2) {
            pinchStartDist = d;
            pinchStartScale = scale;
            return;
          }
          const next = pinchStartScale * (d / pinchStartDist);
          scale = Math.min(SCALE_MAX, Math.max(SCALE_MIN, next));
          clampPan();
          applyTransform();
        } else if (e.touches.length === 1 && touchPan && scale > 1.05) {
          e.preventDefault();
          panX = panStartX + (e.touches[0].clientX - dragStartX);
          panY = panStartY + (e.touches[0].clientY - dragStartY);
          clampPan();
          applyTransform();
        }
      },
      { passive: false },
    );

    function onTouchEnd(e) {
      if (e.touches.length === 0) {
        pinchStartDist = 0;
        touchPan = false;
      } else if (e.touches.length < 2) {
        pinchStartDist = 0;
      }
    }
    stage.addEventListener('touchend', onTouchEnd);
    stage.addEventListener('touchcancel', onTouchEnd);

    window.addEventListener('keydown', (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLb();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex(index + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex(index - 1);
      }
    });

    mqDesktop.addEventListener('change', () => {
      if (!mqDesktop.matches) lens.hidden = true;
    });
  }

  function initVariantUI(root) {
    const variantsJson = root.querySelector('[data-variants-json]');
    if (!variantsJson) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent || '[]');
    } catch {
      variants = [];
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    let variantQuantityMax = {};
    const qtyMaxEl = root.querySelector('[data-variant-quantity-max]');
    if (qtyMaxEl && qtyMaxEl.textContent) {
      try {
        variantQuantityMax = JSON.parse(qtyMaxEl.textContent.trim());
      } catch (_) {}
    }

    const form = root.querySelector('form[data-product-form]');
    const variantIdInput = root.querySelector('input[name="id"][data-variant-id]');
    const priceEl = root.querySelector('[data-product-price]');
    const compareEl = root.querySelector('[data-product-compare-at]');
    const discountEl = root.querySelector('[data-product-discount]');
    const atcBtn = root.querySelector('button[type="submit"][data-add-to-cart]');
    const buyNowBtn = root.querySelector('button[type="submit"][data-buy-now]');

    const optionButtons = Array.from(root.querySelectorAll('button[data-option-position][data-option-value]'));

    const selected = new Map();

    function isButtonValueAvailable(btn) {
      const pos = Number(btn.getAttribute('data-option-position') || 0);
      const val = btn.getAttribute('data-option-value') || '';
      if (!pos) return true;

      // Consider current selection for all other positions, but force this button's position to its value.
      return variants.some((v) => {
        if (!v || !v.available || !Array.isArray(v.options)) return false;
        if (v.options[pos - 1] !== val) return false;

        for (const [p, sel] of selected.entries()) {
          if (!sel) continue;
          if (p === pos) continue;
          if (v.options[p - 1] !== sel) return false;
        }
        return true;
      });
    }

    function setAvailabilityStates() {
      optionButtons.forEach((btn) => {
        const available = isButtonValueAvailable(btn);
        // "Sold out" here means: given current selection context, no AVAILABLE variant exists for this value.
        btn.classList.toggle('is-soldout', !available);
        btn.setAttribute('aria-disabled', !available ? 'true' : 'false');
        btn.setAttribute('data-soldout', !available ? 'true' : 'false');
      });
    }

    function findVariant() {
      const ordered = [];
      const maxPos = Math.max(
        0,
        ...optionButtons.map((b) => Number(b.getAttribute('data-option-position') || 0))
      );
      for (let i = 1; i <= maxPos; i++) ordered.push(selected.get(i) || null);

      return variants.find((v) => {
        // Shopify product JSON gives options as an array aligned to product options.
        if (!Array.isArray(v.options)) return false;
        for (let i = 0; i < ordered.length; i++) {
          if (ordered[i] && v.options[i] !== ordered[i]) return false;
        }
        return true;
      });
    }

    function setActiveStates() {
      optionButtons.forEach((btn) => {
        const pos = Number(btn.getAttribute('data-option-position') || 0);
        const val = btn.getAttribute('data-option-value') || '';
        const isActive = selected.get(pos) === val;
        btn.classList.toggle('is-active', isActive);
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function updateUI(variant) {
      if (variantIdInput && variant?.id) variantIdInput.value = String(variant.id);

      const moneyFormat = root.getAttribute('data-money-format') || undefined;
      if (priceEl && typeof variant?.price === 'number') priceEl.textContent = formatMoney(variant.price, moneyFormat);

      const hasCompare = typeof variant?.compare_at_price === 'number' && variant.compare_at_price > (variant.price || 0);
      if (compareEl) {
        compareEl.textContent = hasCompare ? formatMoney(variant.compare_at_price, moneyFormat) : '';
      }

      if (discountEl) {
        if (hasCompare && variant?.compare_at_price > 0) {
          const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountEl.textContent = `${discount}% OFF`;
          discountEl.style.display = '';
        } else {
          discountEl.textContent = '';
          discountEl.style.display = 'none';
        }
      }

      const available = !!variant?.available;
      if (atcBtn) atcBtn.disabled = !available;
      if (buyNowBtn) buyNowBtn.disabled = !available;

      updateQtySelect(root, variant, variantQuantityMax);
    }

    // Seed selection from currently-selected variant id (server-rendered) or from URL param.
    let initialVariant = null;
    const url = new URL(window.location.href);
    const urlVariantId = url.searchParams.get('variant');
    if (urlVariantId) initialVariant = variants.find((v) => String(v.id) === String(urlVariantId)) || null;
    if (!initialVariant && variantIdInput?.value) initialVariant = variants.find((v) => String(v.id) === String(variantIdInput.value)) || null;
    if (!initialVariant) initialVariant = variants.find((v) => v.available) || variants[0];

    if (Array.isArray(initialVariant?.options)) {
      initialVariant.options.forEach((val, idx) => selected.set(idx + 1, val));
    }
    setActiveStates();
    setAvailabilityStates();
    updateUI(initialVariant);

    optionButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const pos = Number(btn.getAttribute('data-option-position') || 0);
        const val = btn.getAttribute('data-option-value') || '';
        if (!pos) return;
        selected.set(pos, val);
        setActiveStates();
        setAvailabilityStates();
        const v = findVariant() || initialVariant;
        updateUI(v);
      });
    });
  }

  function setProductSubmitLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!loading);
    btn.disabled = !!loading;
    const def = btn.querySelector('[data-state="default"]');
    const wait = btn.querySelector('[data-state="loading"]');
    if (def) def.classList.toggle('hidden', !!loading);
    if (wait) wait.classList.toggle('hidden', !loading);
  }

  function initAjaxAddToCart(root) {
    const form = root.querySelector('form[data-product-form]');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      const submitter = e.submitter || document.activeElement;
      if (submitter && submitter.matches && submitter.matches('[data-buy-now], button[name="checkout"]')) return;

      e.preventDefault();
      const atcBtn = form.querySelector('[data-add-to-cart]');
      const idInput = form.querySelector('input[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      const id = idInput && idInput.value;
      if (!id) return;
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value ? qtyInput.value : '1', 10) || 1);

      setProductSubmitLoading(atcBtn, true);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(id), quantity })
      })
        .then((res) => {
          if (!res.ok) throw new Error('add failed');
          return fetch('/cart.js', { headers: { Accept: 'application/json' } });
        })
        .then((res) => res.json())
        .then((cart) => {
          setProductSubmitLoading(atcBtn, false);
          if (window.KasuriCart && typeof window.KasuriCart.updateCount === 'function') {
            window.KasuriCart.updateCount(cart.item_count);
          }
          const desktop = window.KasuriCart && typeof window.KasuriCart.isDesktop === 'function'
            ? window.KasuriCart.isDesktop()
            : window.matchMedia && window.matchMedia('(min-width: 1025px)').matches;
          if (desktop && window.KasuriCart && typeof window.KasuriCart.openDrawer === 'function') {
            window.KasuriCart.openDrawer();
            return;
          }
          window.location.href = '/cart';
        })
        .catch(() => {
          setProductSubmitLoading(atcBtn, false);
          form.submit();
        });
    });
  }

  function initLookTabs(root) {
    const looks = root.querySelector('[data-product-looks]');
    if (!looks) return;
    const tabs = Array.from(looks.querySelectorAll('[data-look-tab]'));
    const panels = Array.from(looks.querySelectorAll('[data-look-panel]'));
    if (tabs.length < 2) return;

    function activate(name, focusTab) {
      tabs.forEach((tab) => {
        const on = tab.getAttribute('data-look-tab') === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && focusTab) tab.focus();
      });
      panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-look-panel') !== name;
      });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab.getAttribute('data-look-tab')));
      tab.addEventListener('keydown', (e) => {
        let next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next < 0) return;
        e.preventDefault();
        activate(tabs[next].getAttribute('data-look-tab'), true);
      });
    });
  }

  onReady(() => {
    const root = document.querySelector('[data-product-page]');
    if (!root) return;

    root.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
    initProductLightbox(root);
    initQuantity(root);
    initVariantUI(root);
    initAjaxAddToCart(root);
    initLookTabs(root);
  });
})();


